import { useEffect, useMemo, useRef } from 'react';
import type { JSX } from 'react';
import type { HostBridge } from '../../host.js';
import type { ShellHydration } from '../../api.js';
import { type Direction, questionAnim } from '../../motion/keyframes.js';
import { ErrorText } from '../../_components/ErrorText.js';
import { GradientHeader } from '../../_components/GradientHeader.js';
import { SegmentedProgress } from '../../_components/ProgressBar.js';
import { Shell } from '../../_components/Shell.js';
import { WizardFooter } from '../../_components/WizardFooter.js';
import { StepBody, iconForStepKind } from './_components/StepBody.js';
import { ThanksView } from './_components/ThanksView.js';
import { useCaptureBridge } from './hooks/useCaptureBridge.js';
import { isStepRequirementFilled, useFeedbackWizard } from './hooks/useFeedbackWizard.js';
import { performFeedbackSubmission } from './hooks/useFeedbackSubmission.js';
import {
  resolveFeedbackPalette,
  type Attachment,
  type FeedbackInline,
  type FeedbackWidgetConfig,
} from './types.js';
import { buildSteps, resolveHeaderTitle } from './utils/buildSteps.js';
import { resolveLayoutMode } from './utils/layout.js';

// Backend errors should never reach the form UI verbatim. Map the small set
// of expected codes to user-readable copy; everything else collapses to a
// safe generic line. The raw text still goes to bridge.send for telemetry.
function friendlySubmitError(raw: string): string {
  const text = (raw || '').trim();
  if (!text) return 'Could not submit feedback. Please try again.';
  if (text === 'Feedback is empty') return 'Add a comment or rating before submitting.';
  if (text === 'PROJECT_NOT_FOUND') return 'This widget is not configured. Please contact support.';
  if (text === 'ORIGIN_NOT_ALLOWED') return 'Feedback from this domain is not permitted.';
  if (/rate limit|too many/i.test(text)) return 'Too many submissions — please try again later.';
  if (/network|failed to fetch|timeout/i.test(text)) return 'Network error. Please check your connection and try again.';
  return 'Could not submit feedback. Please try again.';
}

interface FeedbackRendererProps {
  bridge: HostBridge;
  config?: unknown;
  widget?: unknown;
  hydration: ShellHydration;
}

export function FeedbackRenderer({
  bridge,
  config,
  widget,
  hydration,
}: FeedbackRendererProps): JSX.Element {
  const inline = (config as FeedbackInline | undefined) ?? null;
  const widgetConfig = (widget as FeedbackWidgetConfig | undefined) ?? null;
  const palette = useMemo(
    () =>
      resolveFeedbackPalette(
        (inline?.theme as FeedbackWidgetConfig['theme'] | undefined) ?? widgetConfig?.theme,
      ),
    [inline, widgetConfig],
  );
  const steps = useMemo(() => buildSteps(inline, widgetConfig), [inline, widgetConfig]);
  const contentSteps = useMemo(() => steps.filter((step) => step.kind !== 'thanks'), [steps]);
  const thanksStep = useMemo(() => steps.find((step) => step.kind === 'thanks'), [steps]);
  const knownEmail = resolveKnownEmail(hydration);
  const knownFullName = hydration.user?.fullName;

  const [snapshot, actions] = useFeedbackWizard({
    email: knownEmail,
    fullName: knownFullName,
    inline,
  });

  const { requestCapture } = useCaptureBridge({
    bridge,
    onBusy: actions.setBusy,
    onError: actions.setError,
  });

  useEffect(() => {
    bridge.send('chirp:resize', { width: 520, height: 560 });
  }, [bridge]);

  // Emit shown once per mount so customers can subscribe to feedback:shown
  // for parity with survey:shown / announcement:shown. Inline-config opens
  // pass the inline name; the global widget falls back to "global".
  const feedbackShownEmittedRef = useRef(false);
  useEffect(() => {
    if (feedbackShownEmittedRef.current) return;
    feedbackShownEmittedRef.current = true;
    bridge.send('chirp:feedback:shown', { name: inline?.name ?? 'global' });
  }, [bridge, inline?.name]);

  const dismiss = (): void => {
    bridge.send('chirp:feedback:dismissed', {
      name: inline?.name ?? 'global',
      reason: 'user_close',
    });
  };

  const captureAndStore = async (kind: 'screenshot' | 'recording'): Promise<void> => {
    actions.setError(null);
    const attachment: Attachment | null = await requestCapture(kind);
    if (attachment) actions.addAttachment(attachment);
  };

  const submit = async (): Promise<void> => {
    actions.setSubmitting(true);
    actions.setError(null);
    try {
      const ratingStep = contentSteps.find((step) => step.kind === 'rating');
      const outcome = await performFeedbackSubmission({
        hydration,
        attachments: snapshot.attachments,
        rating: snapshot.rating,
        comment: snapshot.comment,
        fullName: snapshot.fullName,
        email: snapshot.email,
        selectedType: snapshot.selectedType,
        inline,
        ratingStyle: ratingStep?.ratingStyle,
      });
      if (outcome.failedCount > 0 && outcome.persistedCount === 0) {
        actions.setError(
          `Could not upload ${outcome.failedCount} attachment${outcome.failedCount === 1 ? '' : 's'}. Continuing without them.`,
        );
      }
      if (outcome.ok) {
        bridge.send('chirp:feedback:submitted', {
          name: inline?.name ?? 'global',
          type: outcome.submittedType,
          response: {
            rating: snapshot.rating,
            comment: snapshot.comment,
            email: snapshot.email,
            fullName: snapshot.fullName,
            attachments: snapshot.attachments.length,
          },
        });
        actions.setSubmitted(true);
      }
    } catch (error) {
      const raw = (error as Error).message ?? '';
      actions.setError(friendlySubmitError(raw));
      bridge.send('chirp:error', { code: 'feedback_submit', message: raw });
    } finally {
      actions.setSubmitting(false);
    }
  };

  const directionRef = useRef<Direction>('forward');
  const autoAdvanceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const cancelAutoAdvance = (): void => {
    if (autoAdvanceTimerRef.current) {
      clearTimeout(autoAdvanceTimerRef.current);
      autoAdvanceTimerRef.current = null;
    }
  };

  useEffect(() => cancelAutoAdvance, []);

  const hasAnyContent = (): boolean => {
    if (snapshot.rating != null) return true;
    if (snapshot.comment.trim().length > 0) return true;
    if (snapshot.attachments.length > 0) return true;
    return false;
  };

  const goNext = async (): Promise<void> => {
    cancelAutoAdvance();
    const activeStep = contentSteps[snapshot.stepIndex];
    if (!isStepRequirementFilled(activeStep, snapshot)) {
      actions.setError('This step is required.');
      return;
    }
    const isLast = snapshot.stepIndex >= contentSteps.length - 1;
    if (isLast && !hasAnyContent()) {
      actions.setError('Add a rating, comment, or attachment before submitting.');
      return;
    }
    actions.setError(null);
    directionRef.current = 'forward';
    const result = actions.advance(contentSteps, { email: knownEmail, fullName: knownFullName });
    if (result === 'submit') await submit();
  };

  const goBack = (): void => {
    cancelAutoAdvance();
    actions.setError(null);
    directionRef.current = 'backward';
    actions.retreat();
  };

  const layout = resolveLayoutMode(widgetConfig?.layoutMode);

  // Advance bypasses re-validation — the caller just set a valid value, but a
  // setTimeout closure here would read the snapshot from BEFORE setState applied
  // and falsely fail isStepRequirementFilled.
  const scheduleAutoAdvance = (delayMs: number): void => {
    cancelAutoAdvance();
    autoAdvanceTimerRef.current = setTimeout(() => {
      autoAdvanceTimerRef.current = null;
      actions.setError(null);
      directionRef.current = 'forward';
      actions.advance(contentSteps, { email: knownEmail, fullName: knownFullName });
    }, delayMs);
  };

  const handleRatingChange = (value: number): void => {
    actions.setRating(value);
    // Previously auto-advanced after 450ms — but that raced with the user
    // clicking Next, causing them to skip Step 2 (Comment) and land directly
    // on Step 3. Now the user explicitly clicks Next; one extra click, but
    // zero chance of accidentally jumping a step.
  };

  const handleTypePicked = (value: string): void => {
    actions.setSelectedType(value);
    scheduleAutoAdvance(220);
  };

  if (snapshot.submitted) {
    const thanksTitle =
      thanksStep?.thanksTitle ??
      widgetConfig?.labels?.thanksTitle ??
      inline?.labels?.thanks ??
      'Thanks for your feedback!';
    const thanksBody =
      thanksStep?.thanksBody ?? widgetConfig?.labels?.thanksBody ?? 'We read every one.';
    return (
      <Shell palette={palette} onDismiss={dismiss} layout={layout}>
        <ThanksView
          palette={palette}
          step={thanksStep}
          title={thanksTitle}
          body={thanksBody}
          onClose={dismiss}
        />
      </Shell>
    );
  }

  const activeStep = contentSteps[snapshot.stepIndex];
  const totalSteps = Math.max(contentSteps.length, 1);
  const isLastStep = snapshot.stepIndex >= contentSteps.length - 1;
  const title = resolveHeaderTitle(activeStep, inline);
  const subtitle = totalSteps > 1 ? `Step ${snapshot.stepIndex + 1} of ${totalSteps}` : undefined;

  return (
    <Shell palette={palette} onDismiss={dismiss} layout={layout}>
      <GradientHeader
        palette={palette}
        title={title}
        subtitle={subtitle}
        icon={iconForStepKind(activeStep?.kind)}
        onClose={dismiss}
        useGradient={palette.useGradient}
      />
      {totalSteps > 1 && (
        <SegmentedProgress
          palette={palette}
          segmentCount={totalSteps}
          activeIndex={snapshot.stepIndex}
        />
      )}
      <div
        // Keying on stepIndex re-mounts the body and replays the slide-in
        // animation. Direction comes from the ref so retreating slides in
        // from the left and advancing from the right.
        key={`step-${snapshot.stepIndex}`}
        style={{
          padding: '20px 24px',
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          gap: 16,
          animation: questionAnim(directionRef.current),
        }}
      >
        {inline?.description && !inline?.title && (
          <p style={{ color: palette.textMuted, margin: 0, fontSize: 14 }}>{inline.description}</p>
        )}
        {activeStep?.question && (
          <h3 style={{ margin: 0, fontSize: 16, fontWeight: 600, color: palette.text }}>
            {activeStep.question}
          </h3>
        )}
        <StepBody
          palette={palette}
          step={activeStep}
          snapshot={snapshot}
          actions={actions}
          inline={inline}
          onRatingChange={handleRatingChange}
          onTypePicked={handleTypePicked}
          onRequestScreenshot={() => captureAndStore('screenshot')}
          onRequestRecording={() => captureAndStore('recording')}
        />
        {snapshot.error && <ErrorText>{snapshot.error}</ErrorText>}
      </div>
      <WizardFooter
        palette={palette}
        onBack={goBack}
        onNext={goNext}
        canGoBack={snapshot.stepIndex > 0}
        isLast={isLastStep}
        submitting={snapshot.submitting}
        submitLabel={inline?.labels?.submit ?? widgetConfig?.labels?.submitButton}
      />
    </Shell>
  );
}

function resolveKnownEmail(hydration: ShellHydration): string | undefined {
  const fromEmail = hydration.user?.email;
  if (fromEmail) return fromEmail;
  const uniqueId = hydration.user?.uniqueId;
  if (uniqueId && uniqueId.includes('@')) return uniqueId;
  return undefined;
}
